import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { TableCell } from '../../components/table/Table';
import LocalDate from '../../components/LocalDate';
import { Tab } from './Tab';
import ActionButton from '../../components/ActionButton';
import FieldCreation from './FieldCreation';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import {
  selectors,
  actions,
} from '../../store/dux/field-boundary-management-dux';
import { actions as alertActions } from '../../store/dux/alerts-dux';
import { useHasChanged } from '../../hooks';
import { usePermissions } from '../../hooks/use-permissions';
import BoundaryEditorDialog from './BoundaryEditorDialog';
import { DeleteFields } from './DeleteFields';
import { EditAttributes } from './EditAttributes';
import { ReassignFields } from './ReassignFields';
import { ValidateFields } from './ValidateFields';
import { CertifyFields } from './CertifyFields';
import { CombineFields } from './CombineFields';
import { UploadBoundary } from './UploadBoundary';
import { exportUncertifiedFields as exportCsv } from '../../services/field-boundary-management-service';
import { useAlerts } from '../../hooks/use-alerts';
import { usePermission } from '../../hooks/use-permission';

export function UncertifiedTab({ isActive, renderTabs }) {
  const dispatch = useDispatch();

  const [newField, setNewField] = useState();
  const [deleteFields, setDeleteFields] = useState();
  const [editBoundary, setEditBoundary] = useState();
  const [editAttributes, setEditAttributesFor] = useState();
  const [reassignFields, setReassignFields] = useState();
  const [validateFields, setValidateFields] = useState();
  const [certifyFields, setCertifyFields] = useState();
  const [combineFields, setCombineFields] = useState();
  const [uploadBoundary, setUploadBoundary] = useState();

  const growerId = useSelector(growerSelectors.selectedId);
  const growerName = useSelector(growerSelectors.selectedName);
  const fields = useSelector(selectors.uncertifiedFields);
  const hasFailed = useSelector(selectors.error);
  const isWorking = useSelector(selectors.isWorking);
  const isWorkingChanged = useHasChanged(isWorking);
  const selectedAaids = useSelector(selectors.selectedFields);
  const canUploadBoundary = usePermissions().includes('UPLOAD_BOUNDARY');

  const findByAaid = (aaid) => fields?.find((f) => f.aaManagedId === aaid);
  const selectedFields = selectedAaids?.map(findByAaid);
  const selectedField = selectedFields?.[0];
  const isAnySelected = selectedAaids.length > 0;
  const isManySelected = selectedAaids.length > 1;
  const isOneSelected = selectedAaids.length === 1;
  const permissions = usePermissions();
  const allowedToReassign = permissions.includes('ADMIN_OPERATIONS');
  const showActions = usePermission('VIEW_ACTIONS');
  const { showInfo } = useAlerts();

  const renderActions = useCallback(
    () => (
      <ActionButton>
        <Button onClick={() => setNewField({})}>Add New</Button>
        <Button
          onClick={() => setEditAttributesFor(selectedAaids)}
          disabled={!isAnySelected}
        >
          Edit Attributes
        </Button>
        <Button
          onClick={() => setEditBoundary(selectedField)}
          disabled={!isOneSelected}
        >
          Edit Boundary
        </Button>
        {canUploadBoundary ? (
          <Button
            onClick={() => setUploadBoundary(selectedField)}
            disabled={!isOneSelected}
          >
            Upload Boundary
          </Button>
        ) : null}
        <Button
          onClick={() => setValidateFields(selectedAaids)}
          disabled={!isAnySelected}
        >
          Validate
        </Button>
        <Button
          onClick={() => setCertifyFields(selectedFields)}
          disabled={!isAnySelected}
        >
          Certify
        </Button>
        <Button
          onClick={() => setDeleteFields(selectedFields)}
          disabled={!isAnySelected}
        >
          Delete
        </Button>
        <Button
          onClick={() => setCombineFields(selectedFields)}
          disabled={!isManySelected}
        >
          Combine
        </Button>
        <Button
          onClick={() => exportCsv(isAnySelected ? selectedFields : fields)}
          disabled={!fields?.length}
        >
          Export CSV
        </Button>
        {allowedToReassign ? (
          <Button
            onClick={() => setReassignFields(selectedAaids)}
            disabled={!isAnySelected}
          >
            Reassign Fields
          </Button>
        ) : null}
      </ActionButton>
    ),
    [
      canUploadBoundary,
      fields,
      isAnySelected,
      isManySelected,
      isOneSelected,
      selectedAaids,
      selectedField,
      selectedFields,
      allowedToReassign,
    ],
  );

  useEffect(() => {
    if (hasFailed) {
      dispatch(alertActions.showError(hasFailed));
      dispatch(actions.clearFailure());
    }
  }, [hasFailed, dispatch]);

  useEffect(() => {
    if (isWorkingChanged && !isWorking && !hasFailed && editBoundary) {
      setEditBoundary(null);
    }
  }, [editBoundary, hasFailed, isWorking, isWorkingChanged]);

  return (
    <>
      {newField && buildFieldCreate()}
      {editBoundary && buildEditBoundary()}
      {editAttributes && buildEditAttributes()}
      {deleteFields && buildDeleteFields()}
      {validateFields && buildValidateFields()}
      {certifyFields && buildCertifyFields()}
      {combineFields && buildCombineFields()}
      {uploadBoundary && buildUploadBoundary()}
      {reassignFields && buildReassignFields()}

      <Tab
        type="uncertified"
        columns={buildColumns()}
        isActive={isActive}
        renderTabs={renderTabs}
        renderActions={showActions ? renderActions : undefined}
      />
    </>
  );

  function buildFieldCreate() {
    return (
      <FieldCreation
        field={newField}
        growerName={growerName}
        save={setEditBoundary}
        close={() => setNewField(null)}
      />
    );
  }

  function buildEditBoundary() {
    return (
      <BoundaryEditorDialog
        field={editBoundary}
        growerId={growerId}
        save={saveField}
        close={closeBoundaryEditor}
      />
    );
  }

  function buildEditAttributes() {
    return (
      <EditAttributes
        fieldsInfo={{ type: 'UNCERTIFIED', aaManagedIds: editAttributes }}
        save={saveAttributes}
        close={() => setEditAttributesFor(null)}
      />
    );
  }

  function buildReassignFields() {
    return (
      <ReassignFields
        fieldsToReassign={reassignFields}
        close={() => setReassignFields(null)}
        save={saveReassignedFields}
      />
    );
  }

  function saveReassignedFields(newGrowerId, fieldsToReassign) {
    dispatch(actions.reassignFields(growerId, newGrowerId, fieldsToReassign));
    showInfo('Field reassignments have been scheduled.');
  }

  function buildDeleteFields() {
    return (
      <DeleteFields fields={deleteFields} close={() => setDeleteFields(null)} />
    );
  }

  function buildValidateFields() {
    return (
      <ValidateFields
        aaids={validateFields}
        close={() => setValidateFields(null)}
      />
    );
  }

  function buildCertifyFields() {
    return (
      <CertifyFields
        fields={certifyFields}
        close={() => setCertifyFields(null)}
      />
    );
  }

  function buildCombineFields() {
    return (
      <CombineFields
        fields={combineFields}
        close={() => setCombineFields(null)}
      />
    );
  }

  function buildUploadBoundary() {
    return (
      <UploadBoundary
        field={uploadBoundary}
        handleSave={saveField}
        handleClose={() => setUploadBoundary(null)}
      />
    );
  }

  function saveAttributes(fieldsInfo, attributes) {
    dispatch(actions.saveAttributes(growerId, fieldsInfo, attributes));
  }

  function closeBoundaryEditor() {
    setEditBoundary(null);
    dispatch(actions.resetBoundaryChanges());
  }

  function saveField(field) {
    dispatch(
      actions.saveField({
        ...field,
        growerOperationId: growerId,
        growerOperationName: growerName,
      }),
    );
  }
}

function buildColumns() {
  return [
    {
      field: 'validated',
      headerName: 'Validated',
      flex: 0.5,
    },
    {
      field: 'validateError',
      headerName: 'Validation Error',
      flex: 2,
    },
    {
      field: 'createdDate',
      type: 'date',
      headerName: 'Date Uploaded',
      flex: 1,
      valueGetter: ({ value }) => value && new Date(value),
      renderCell: (params) => (
        <TableCell renderCellParams={params}>
          <LocalDate value={params.row.createdDate} />
        </TableCell>
      ),
    },
  ];
}
